import React, { useCallback, useState } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';

type FormDataType = {
  name: string;
  description: string;
  fileName: string;
  variationid: number;
  file: {};
};

interface AddNewFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  count: number;
  setCount: any;
}

const getColor = (props: {
  isDragAccept: any;
  isDragReject: any;
  isFocused: any;
}) => {
  if (props.isDragAccept) {
    return '#00e676';
  }
  if (props.isDragReject) {
    return '#ff1744';
  }
  if (props.isFocused) {
    return '#2196f3';
  }
  return '#eeeeee';
};

function AddNewFormModal({
  isOpen,
  onClose,
  count,
  setCount,
}: AddNewFormModalProps) {
  let toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fileName: '',
    variationid: 0,
    file: {},
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = new FileReader();

      file.readAsDataURL(acceptedFiles[0]);

      setFormData(prevFormData => ({
        ...prevFormData,
        fileName: acceptedFiles[0].name,
        file: acceptedFiles[0],
      }));
    },
    [setFormData]
  );

  const {
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': [],
    },
    maxFiles: 1,
  });

  const { accounts } = useMsal();
  const userdetails = JSON.parse(localStorage.getItem('userdetails') || '');
  const userid =
    userdetails[0].portal_Id != ''
      ? userdetails[0].portal_Id
      : accounts[0].username;

  const addDocument = async (data: FormDataType, setCount: any, count: any) => {
    const tokenUrl = process.env.REACT_APP_TOKEN_URL_PAYER_FORM as string;
    const baseurl = process.env.REACT_APP_PAYER_FORM_BASE_URL as string;

    try {
      // Fetch token from tokenUrl
      const tokenResponse = await axios.get(tokenUrl);
      const tokenData = tokenResponse.data;
      const token = tokenData.token;

      // Post data to saveFormUrl using Axios
      const response = await axios.post(
        baseurl + '/App/api/add_form?username=' + userid,
        {
          name: data.fileName,
          description: data.description,
          file: data.file,
        },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        // Data successfully posted
        setCount(count + 1);
        toast({
          title: 'Success',
          position: 'top',
          description: 'Document Successfully Added!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Handle error if data couldn't be posted
        console.error('Error adding data');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const borderColor = getColor({ isDragAccept, isDragReject, isFocused });

  const fileRejectionItems = fileRejections.map(({ file, errors }, index) => (
    <Text key={index} color="red.500">
      {file.name} -{' '}
      {errors
        .map(error => {
          if (error.code === 'file-invalid-type') {
            return `File type must be .pdf`;
          }
          return error.message;
        })
        .join(', ')}
    </Text>
  ));

  const handleSubmit = (event: any) => {
    event.preventDefault(); // Prevent the default form submission behavior
    addDocument(formData, setCount, count);
    setFormData({
      name: '',
      description: '',
      fileName: '',
      variationid: 0,
      file: {},
    });
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          addDocument(formData, setCount, count);
          setFormData({
            name: '',
            description: '',
            fileName: '',
            variationid: 0,
            file: {},
          });
          onClose();
        }}
      >
        <ModalOverlay
          onClick={() => {
            setFormData({
              name: '',
              description: '',
              fileName: '',
              variationid: 0,
              file: {},
            });
            onClose();
          }}
        />
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <ModalHeader textAlign="center">Add New Payer Form</ModalHeader>
            <ModalCloseButton color="white" bg=" #002677" />
            <ModalBody>
              <FormControl isRequired>
                <FormLabel>Name:</FormLabel>
                <Input
                  placeholder="Name"
                  onChange={event => {
                    const newFormName = event.target.value;
                    setFormData(prevFormData => ({
                      ...prevFormData,
                      name: newFormName,
                    }));
                  }}
                />
              </FormControl>
              <FormControl mt={4} isRequired>
                <FormLabel>Description:</FormLabel>
                <Input
                  placeholder="Description"
                  onChange={event => {
                    const newFormDesc = event.target.value;
                    setFormData(prevFormData => ({
                      ...prevFormData,
                      description: newFormDesc,
                    }));
                  }}
                />
              </FormControl>
              <FormControl mt={4} isRequired>
                <FormLabel>PDF File:</FormLabel>
                <Button
                  onClick={open}
                  fontSize="12px"
                  w="fit-content"
                  h="25px"
                  bg="#002677"
                  textColor="white"
                  mb="5px"
                >
                  Upload File
                </Button>
                <Flex
                  {...getRootProps()}
                  flex="1"
                  flexDirection="column"
                  alignItems="center"
                  p="20px"
                  borderWidth="2px"
                  borderRadius="2px"
                  borderColor={borderColor}
                  borderStyle="dashed"
                  backgroundColor="#fafafa"
                  color="#bdbdbd"
                  outline="none"
                  transition="border .24s ease-in-out"
                >
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <Text>Drop the files here ...</Text>
                  ) : (
                    <Flex display="flex" flexDir="column">
                      <Text textAlign="center">Drag and drop here ...</Text>
                      <Text textAlign="center">
                        Only *.pdf files will be accepted!
                      </Text>
                    </Flex>
                  )}
                </Flex>
                <Flex>
                  {fileRejectionItems.length != 0
                    ? fileRejectionItems
                    : formData.fileName}
                </Flex>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button
                bg="#002677"
                textColor="white"
                mr={3}
                onClick={() => {
                  setFormData({
                    name: '',
                    description: '',
                    fileName: '',
                    variationid: 0,
                    file: {},
                  });
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Spacer />
              <Button
                variant="ghost"
                bg="#002677"
                textColor="white"
                type="submit"
              >
                Add Form
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
}

export default AddNewFormModal;
